/**
 * Workflow-step type contract.
 *
 * Invariants:
 * - Every step declares both input and output schemas.
 * - `defineStep` returns immutable step metadata consumed by workflow runtime.
 *
 * @see `packages/core/docs/ai/agents.md`
 */
import { z } from "zod/v4";

export type Step<StepName extends string = string, Input = unknown, Output = unknown> = {
  name: StepName;
  inputSchema: z.ZodType<Input>;
  outputSchema: z.ZodType<Output>;
};

export type AnyStep = Step<string, any, any>;

export type InferStepInput<S extends AnyStep> = z.infer<S["inputSchema"]>;
export type InferStepOutput<S extends AnyStep> = z.infer<S["outputSchema"]>;

type DefineStepArgs<StepName extends string, Input, Output> = {
  name: StepName;
  inputSchema: z.ZodType<Input>;
  outputSchema: z.ZodType<Output>;
};

export function defineStep<StepName extends string, Input, Output>(
  args: DefineStepArgs<StepName, Input, Output>,
): Step<StepName, Input, Output> {
  return {
    name: args.name,
    inputSchema: args.inputSchema,
    outputSchema: args.outputSchema,
  };
}
